import { Inject, Injectable } from '@angular/core';
import { each, filter, find, includes, isEmpty, isNumber, reduce, startCase, toLower, upperFirst } from 'lodash';
import { ICsvDataState } from 'src/ng2/store/reducers/csv-data-reducer';
import { TValidJoins } from '../../../../app/sdc/sdc-state/sdc-state.typings';
import { NextRegentsAdminDateLetter } from '../../../../ng2/shared/constants/next-regents-admin-date-letter.constant';
import { RegentsExamCodes } from '../../../../ng2/shared/constants/regents-exam-codes.constant';
import { RegentsExam } from '../../../../ng2/shared/constants/regents.constant';
import { IGapPlan } from '../../../../ng2/shared/typings/interfaces/gap-plan.interface';
import { ISchool } from '../../../../ng2/shared/typings/interfaces/school.interface';
import { IStudent } from '../../../../ng2/shared/typings/interfaces/student.interface';
import { ICourseDiff } from '../../typings/interfaces/course-diff.interface';
import { DateHelpers } from '../date-helpers/date-helpers.service';
import { ImCourseDiff } from '../im-models/im-course-diff';
import { ImSchool } from '../im-models/im-school';
import { COLUMN_DATA_TYPE } from './../../constants/list-view/cell-type.constant';

interface IRegentsStarsExportStudent {
  studentId: string;
  nextScheduledRegents: any;
  status: string;
  last: string;
  first: string;
  currGradeLevel: string;
  officialClass: string;
  classOf: string;
}
/* istanbul ignore next */
@Injectable()
export class CsvExporterService {
  private readonly lineBreak = ',\r\n';
  private readonly sharedHeaders = 'Student ID,' + 'Student Name,' + 'Group,';
  private readonly assessmentDateHeader = 'Assessment Date';

  constructor(
    @Inject('FileSaver') private FileSaver: any,
    @Inject('StudentSet') private StudentSet,
    private dateHelper: DateHelpers,
    private ImSchool: ImSchool,
    private imCourseDiff: ImCourseDiff,
  ) {}

  createCourseDiffCsv(school: ISchool): Promise<string> {
    const currentTermYear = this.ImSchool.getCurrentTermYear(school);

    const header = ['StudentID', 'LastName', 'FirstName', 'GradeLevel', 'OfficialClass', 'Course', 'Section', 'Action'];
    let csvRows = header + '\n';

    return new Promise((resolve, reject) => {
      const schoolId = school._id;
      const joins: TValidJoins[] = ['pendingCourseDiffs'];

      const columnKeys = ['studentId', 'firstName', 'lastName', 'grade', 'officialClass'];

      const forceDataRefetch = true;
      this.StudentSet.fetchStudents({ schoolId, columnKeys, joins, forceDataRefetch })
        .then(({ students }: { students: IStudent[] }) => {
          const studentsWithCourseDiffs = filter(students, student => {
            return !isEmpty(student.join_pendingCourseDiffs);
          });
          each(studentsWithCourseDiffs, student => {
            const currentTermCourseDiffs = this.imCourseDiff.filterForPendingCurrentTerm(
              student.join_pendingCourseDiffs,
              currentTermYear,
            );
            each(currentTermCourseDiffs, (courseDiff: ICourseDiff) => {
              const masterSchedData = find(school.masterSchedule, { courseId: courseDiff.courseId });
              if (!masterSchedData) {
                return;
              }
              csvRows +=
                [
                  student.studentId,
                  student.studentDetails.name.last,
                  student.studentDetails.name.first,
                  student.studentDetails.currGradeLevel,
                  student.studentDetails.officialClass,
                  masterSchedData.courseCode,
                  masterSchedData.section,
                  courseDiff.action,
                ] + '\n';
            });
          });

          if (csvRows) {
            resolve(csvRows);
          } else {
            const error = new Error(`csvRows is undefined`);
            reject(error);
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  createNextTermPlanningCsv(school: ISchool, nextTerm: Number): Promise<string> {
    const header = ['StudentID', 'LastName', 'FirstName', 'GradeLevel', 'OfficialClass', 'Course', 'Section', 'Action'];
    let csvRows = header + '\n';

    return new Promise((resolve, reject) => {
      const schoolId = school._id;
      const joins: TValidJoins[] = ['activeGapPlans'];

      const columnKeys = ['studentId', 'firstName', 'lastName', 'grade', 'officialClass'];

      const forceDataRefetch = true;
      this.StudentSet.fetchStudents({ schoolId, columnKeys, joins, forceDataRefetch })
        .then(({ students }: { students: IStudent[] }) => {
          const studentsWithGapPlan = filter(students, student => {
            return !isEmpty(student.join_activeGapPlans);
          });

          each(studentsWithGapPlan, student => {
            each(student.join_activeGapPlans, (gapPlan: IGapPlan) => {
              if (gapPlan.termYear !== nextTerm || gapPlan.status !== 'ACTIVE') {
                return;
              }

              csvRows +=
                [
                  student.studentId,
                  student.studentDetails.name.last,
                  student.studentDetails.name.first,
                  student.studentDetails.currGradeLevel,
                  student.studentDetails.officialClass,
                  gapPlan.plan.replace(/,/g, ' ').toUpperCase(),
                  '',
                  'ADD',
                ] + '\n';
            });
          });

          if (csvRows) {
            resolve(csvRows);
          } else {
            const error = new Error(`csvRows is undefined`);
            reject(error);
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  createCourseDiffandGapPlanTableCsv(table: any[]) {
    const header = [
      'StudentId',
      'LastName',
      'FirstName',
      'CreatedBy',
      'Created',
      'Course',
      'CreditValue',
      'GradReq',
      'Type',
      'Term',
      'Status',
      'Note',
    ];

    // Wrap vals in quotes to avoid row shift when value includes a comma
    const formatVal = val => {
      return val && `"${val}"`;
    };

    let csvRows = header + '\n';
    each(table, row => {
      let studentName = row.studentName.split(',');
      let createdBy = row.createdBy
        .split(',')
        .reverse()
        .join(' ');
      csvRows +=
        [
          formatVal(row.studentId),
          formatVal(studentName[0]),
          formatVal(studentName[1]),
          formatVal(createdBy),
          formatVal(row.createdAt),
          formatVal(row.courseOrplan),
          formatVal(row.credits),
          formatVal(row.gradReq),
          formatVal(row.type),
          formatVal(row.term),
          formatVal(row.status),
          formatVal(row.note),
        ] + '\n';
    });
    return csvRows;
  }

  createStarsRegentsScheduleExport(flattenedStudents: IRegentsStarsExportStudent[]): string {
    const header = ['StudentID', 'LastName', 'FirstName', 'GradeLevel', 'OfficialClass', 'Course', 'Section', 'Action'];
    let headerRow = header + '\n';
    const csvString = reduce(
      flattenedStudents,
      (result, student: any) => {
        const {
          studentId,
          examsSchedInPortalNotPlannedInStars,
          examsToBeDroppedFromStars,
          nextScheduledLoteExamName,
          status,
          lastName,
          firstName,
          grade,
          officialClass,
          cohort,
        } = student;

        // only include active students
        if (status === 'A') {
          // loop through adds
          result += reduce(
            examsSchedInPortalNotPlannedInStars,
            (csvRows: string, shortName: string) => {
              const regentsExam = find(RegentsExam, { shortName });
              const examKey = regentsExam.key;
              const course = this._getCourseCodeForExamKey(examKey, cohort, nextScheduledLoteExamName);
              csvRows += this._getCsvRow(studentId, lastName, firstName, grade, officialClass, course, 'ADD');
              csvRows += '\n';
              return csvRows;
            },
            '',
          );

          // loop through drops
          result += reduce(
            examsToBeDroppedFromStars,
            (csvRows: string, shortName: string) => {
              const regentsExam = find(RegentsExam, { shortName });
              const examKey = regentsExam.key;
              const course = this._getCourseCodeForExamKey(examKey, cohort, nextScheduledLoteExamName);
              csvRows += this._getCsvRow(studentId, lastName, firstName, grade, officialClass, course, 'DROP');
              csvRows += '\n';
              return csvRows;
            },
            '',
          );
        }

        return result;
      },
      headerRow,
    );
    return csvString;
  }

  _getCourseCodeForExamKey = (examKey, cohort, nextScheduledLoteExamName) => {
    let course;
    if (examKey === 'lote') course = RegentsExamCodes.LOTE[nextScheduledLoteExamName];
    else if (cohort) course = RegentsExamCodes.HIGH_SCHL[examKey];
    else course = RegentsExamCodes.MIDDLE_SCHL[examKey];
    if (course) course += NextRegentsAdminDateLetter;
    return course;
  };

  _getSectionCodeForExamKey(examKey, regentsExemptionStatusByExams) {
    const status = regentsExemptionStatusByExams[examKey];

    switch (status) {
      case 'Completed':
        return '91';
      case 'Completed (DOE)':
        return '91';
      case 'In progress':
        return '92';
      case 'In progress (DOE)':
        return '92';
      default:
        return '';
    }
  }
  /**
   * This calculation depends on the following Sorter Column Service calculations: (Carlos)
   * schedInStarsCcEla
   * schedInStarsCcAlg
   * schedInStarsCcGeom
   * schedInStarsCcTrig
   * schedInStarsLivingEnv
   * schedInStarsEarth
   * schedInStarsChem
   * schedInStarsPhysics
   * schedInStarsGlobal
   * schedInStarsGlobalTwo
   * schedInStarsUs
   * schedInStarsUsFramework
   * schedInStarsLote
   */
  _getSectionCodeForSchedInStarsExamKey(examKey, student) {
    const schedInStarsStatus = student[`schedInStars${upperFirst(examKey)}`];
    const sectionCode = schedInStarsStatus && schedInStarsStatus.split(' ')[1];

    if (sectionCode) {
      const _sectionCode = sectionCode.replace(/[()]/g, ''); // remove parentheses, return '91' or '92' (Carlos)

      return _sectionCode;
    } else {
      const { regentsExemptionStatusByExams } = student;
      return this._getSectionCodeForExamKey(examKey, regentsExemptionStatusByExams);
    }
  }

  _getCsvRow = (studentId, lastName, firstName, grade, officialClass, course, action) => {
    return [studentId, lastName, firstName, grade, officialClass, course, '', action];
  };

  exportCsv(csvString: string, fileName: string, docType: string): void {
    const file_name = fileName;
    const blob = new Blob([csvString], {
      type: docType,
    });
    const disableAutoBOM = true;
    this.FileSaver.saveAs(blob, file_name, disableAutoBOM);
  }

  exportRefactoredListToCsv(csvListData: any) {
    const { madlibSelections, groupings, columns, listType } = csvListData;

    const [_, ...otherCols] = columns;

    const headers = ['Student Id', 'Last Name', 'First Name', ...otherCols.map(col => col.label), 'Group'];

    const todaysDate = this.dateHelper.getMonthDayYear();
    const fileName = `${listType} ${todaysDate} ${madlibSelections.filter.label} focused on ${
      madlibSelections.focus.label
    }`;

    const csvString = groupings.reduce((accum, { rowData, human: groupVal }) => {
      rowData.forEach(row => {
        const [studentColumn, ...rest] = row;
        const otherColVals = rest.map(col => {
          // cell display service stores data replaced by icons in 'originalData' (JE)
          if (col.data === '' && col.originalData) return col.originalData;
          else if (col.data === '—') return '-';
          // coercing to dash so Excel reads it properly
          else return col.data;
        });
        const studentId = JSON.parse(studentColumn.meta).data.slice(0, 9);
        const [lastName, firstName] = studentColumn.data.split(',');

        /**
         * Note: To preserve the values of the variables when exporting, some variables are wrapped in ""
         * These variables may contain commas, which is what the csv delinates cells by.
         * Wrapping the variables in "" ensures the variable get exported to only one cell in the row. (AB)
         */
        const csvRow = [studentId, lastName, firstName, otherColVals.join(','), `"${groupVal}"`].join(',');
        accum += `${csvRow}\n`;
      });
      return accum;
    }, `${headers}\n`);

    // create export
    this.exportCsv(csvString, `${fileName}.csv`, 'csv');
  }

  exportListToCsv(csvStateData: ICsvDataState) {
    const {
      listData,
      madLibSelections: { Dimension1, Dimension2, Groupings },
    } = csvStateData;
    const columnNames = Object.keys(listData.columns);
    let concatData = '';
    const columns = this.concatColNames(columnNames, listData);
    concatData += columns;

    listData.sections.forEach(section => {
      section.data.forEach((student: { studentName: { dependencies: any; data: any } }) => {
        const { studentId } = student.studentName.dependencies;
        const studentName = student.studentName.data;
        const sectionName = section.name;
        concatData += `${studentId},"${studentName}",${sectionName},`;
        const args = { student, columnNames, listData };
        const studentString = this.parseColData(args);
        concatData += studentString;
      });
    });
    this.prepForExport({ concatData, listData, Dimension1, Dimension2, Groupings });
  }

  concatColNames(columnNames: string[], listData: any) {
    let concatHeaders = '';
    columnNames.forEach(col => {
      // land postsec view columns in csv (SR)
      if (listData.listType === 'POSTSECONDARY') {
        // tooltipShort (column titles) are stored on each entry, so hasNotRun prevents column gen for each instance
        let hasNotRun = true;
        listData.sections.forEach(el => {
          // check el.count because empty sections do not contain tooltipShort
          if (el.count > 0 && hasNotRun) {
            // grab appropriate tooltipShort once
            let header = el.data[0][col].dependencies.tooltipShort;
            concatHeaders += `${header},`;
            hasNotRun = false;
          }
        });
      } else {
        // some lists have inconsistent column header casing (SR)
        const colHeader = listData.columns[col].name;
        if (includes(colHeader, 'iReady') || includes(colHeader, 'YTD')) {
          concatHeaders += `${listData.columns[col].name},`;
        } else {
          const humanColHeader = startCase(toLower(colHeader));
          concatHeaders += `${humanColHeader},`;
        }
        // Include assessment date for F&P Scores
        if (
          listData.columns[col].cellType === COLUMN_DATA_TYPE.F_P_SCORE &&
          !concatHeaders.includes(this.assessmentDateHeader)
        ) {
          concatHeaders += `${this.assessmentDateHeader},`;
        }
      }
    });
    return this.sharedHeaders + concatHeaders + this.lineBreak;
  }

  parseColData(args: { student: object; columnNames: string[]; listData }) {
    const { columnNames, student, listData } = args;
    let studentString = '';
    columnNames.forEach(col => {
      if (col !== this.assessmentDateHeader) {
        const cellType = listData.columns[col].cellType;
        let colValue;
        const colData = student[col].data;

        let data;
        let calculatedStatus;
        if (colData === null) {
          data = null;
          calculatedStatus = null;
        } else {
          ({ data, calculatedStatus } = colData);
        }
        switch (cellType) {
          case 'DATE':
            colValue = colData ? `${colData.substr(0, 10)},` : ',';
            break;
          case 'F_P_SCORE':
            switch (col) {
              case 'relScore':
              case 'instructScore':
                if (data !== '-' && colData.date !== null) {
                  colValue = `${data},${colData.date},`;
                } else if (data !== '-') {
                  colValue = `${data},,`;
                } else {
                  colValue = ',,';
                }
                break;
              case 'expectScore':
                colValue = colData && calculatedStatus !== null ? `${calculatedStatus},` : ',';
                break;
              default:
                colValue = ',';
                break;
            }
            break;
          case 'FINANCIAL_AID':
            colValue = `${calculatedStatus},`;
            break;
          case 'FLAG':
            colValue = colData && colData.length ? `${colData.length},` : ',';
            break;
          case 'GENERIC':
            if (colData === '-') {
              colValue = ',';
            } else if (colData) {
              colValue = `${colData},`;
            } else {
              colValue = ',';
            }
            break;
          case 'GRADIENT':
            if (colData === 0) {
              colValue = 'Perfect,';
            } else if (colData > 0) {
              colValue = 'Missed ' + `${colData},`;
            }
            break;
          case 'GRADIENT_CUSTOM':
            const colName = listData.columns[col].name;
            if (colName === 'Attendance Today' || colName === 'Total Failing') {
              colValue = colData ? `${colData},` : ',';
            } else if (includes(colName, 'iReady Growth')) {
              // separates zeros from nulls
              colValue = isNumber(colData) ? `${colData},` : ',';
            } else if (includes(colName, 'Relative')) {
              colValue = colData.latestRelLvl ? `${colData.latestRelLvl},` : ',';
            } else if (colName === 'YTD Attendance Change') {
              colValue = `${colData},`;
            } else {
              colValue = colData ? `${colData},` : '0';
            }
            break;
          case 'MORE_INFO':
            if (colData === '-') {
              colValue = ',';
              break;
            }
            const splitHTML = colData.split('<span class="tooltip-pill">');
            const extractedText = splitHTML.reduce((acc: string, htmlSnippet: string, index: number) => {
              return index === 0 || index === splitHTML.length - 1
                ? acc + this.extractContent(htmlSnippet)
                : acc + this.extractContent(htmlSnippet) + '/';
            }, '');
            colValue = extractedText;
            break;
          case 'OTHER_STAFF':
            colValue = colData.length ? `${colData.length},` : ',';
            break;
          case 'PERCENTAGE':
            colValue = `${colData}%,`;
            break;
          case 'POINT_PERSON':
            colValue = colData !== '—' ? `${colData},` : ',';
            break;
          case 'READING_GROWTH':
            colValue = colData && data !== '-' ? `${data},` : ',';
            break;
          case 'STUDENT_PATH':
            const matched = colData.matchedPaths;
            const total = colData.totalPaths;
            const decision = colData.decision;
            if (listData.columns[col].name === 'Acceptances') {
              colValue = matched.length ? `${matched.length},` : ',';
            } else if (matched) {
              const completed = matched.length / total.length;
              colValue = isNaN(completed) ? ',' : (colValue = `${matched.length}/${total.length}\t,`);
            } else if (decision) {
              colValue = decision === '-' ? `,` : `${decision},`;
            }
            break;
          case 'STATE_EXAM' || 'IREADY':
            colValue = colData.latestScore ? `${colData.latestScore},` : ',';
            break;
          default:
            colValue = `${colData},`;
            break;
        }
        studentString += colValue;
      }
    });
    return `${studentString}${this.lineBreak}`;
  }

  extractContent(s: string): string {
    const span = document.createElement('span');
    span.innerHTML = s;
    return span.textContent || span.innerText;
  }

  prepForExport(args: {
    concatData: string;
    listData: { listType: string };
    Dimension1: string;
    Dimension2: string;
    Groupings: string;
  }) {
    const { concatData, listData, Dimension1, Dimension2, Groupings } = args;
    const csvStr = concatData;
    const humanListType = this.humanListType(listData.listType);
    // remove 'Alt' from filenames that include Postsecondary (SR)
    const listType = humanListType === 'Postsecondary Alt' ? 'Postsecondary' : humanListType;
    const todaysDate = this.dateHelper.getMonthDayYear();
    let fileName = `${listType} ${todaysDate} ${Dimension1} focused on ${Dimension2}`;
    // Check if 'grouped by' section of mad lib is present
    if (Groupings) fileName += ` grouped by ${Groupings}`;
    this.exportCsv(csvStr, `${fileName}.csv`, 'csv');
  }

  humanListType(listType: string) {
    const listTypeArr = listType.split('_').map(el => startCase(toLower(el)));
    return listTypeArr.join(' ');
  }
}
